<template>
  <ModalView
    v-model="show"
    title="New Letter"
    ok-title="Create"
    :disable-ok="!selectedTemplate"
    :busy="creating"
    @ok="onCreate"
  >
    <!-- Template selection -->
    <div :class="{ 'mb-3 pb-3 border-bottom': !loadingListings && listings.length > 0 }">
      <div class="section-label">Template</div>
      <div class="d-flex flex-column gap-2">
        <RadioCard
          v-for="(option, index) in printOptions"
          :key="option.label"
          :label="option.label"
          :description="option.description"
          :selected="selectedTemplateIndex === index"
          :disabled="option.disabled"
          @select="selectedTemplateIndex = index"
        />
      </div>
    </div>

    <!-- Listing selection -->
    <div v-if="!loadingListings && listings.length > 0">
      <div class="section-label">Reference listing</div>
      <div class="section-sublabel">
        Select which listing this letter cites. All active listings are subject to enforcement.
      </div>
      <div class="d-flex flex-column gap-2">
        <RadioCard
          label="None"
          description="No listing attached to this letter"
          :selected="selectedListingId === null"
          @select="selectedListingId = null"
        />
        <RadioCard
          v-for="listing in listings"
          :key="listing.id"
          :label="listing.title || listing.url || listing.id"
          :selected="selectedListingId === listing.id"
          @select="selectedListingId = listing.id"
        >
          <div class="d-flex align-items-center gap-1 mt-1">
            <span v-if="listing.url" class="listing-url text-truncate">{{ listing.url?.replace(/^https?:\/\//, '') }}</span>
            <StatusBadge v-if="listing.status === 'inactive'" variant="disabled" compact>
              Inactive
            </StatusBadge>
            <StatusBadge v-if="listing.evidence?.status === 'captured'" variant="success" compact>
              <i class="bi bi-check"/> Screenshot captured
            </StatusBadge>
            <StatusBadge v-else-if="listing.evidence?.status === 'manual_upload'" variant="success" compact>
              <i class="bi bi-check"/> Screenshot uploaded
            </StatusBadge>
            <StatusBadge v-else-if="listing.evidence?.status === 'failed'" variant="danger" compact>
              <i class="bi bi-check"/> Screenshot failed
            </StatusBadge>
            <StatusBadge v-else variant="warning" compact>
              <i class="bi bi-slash-circle"/> No screenshot
            </StatusBadge>
          </div>
        </RadioCard>
      </div>
    </div>

    <div v-if="loadingListings" class="d-flex align-items-center gap-2 small text-muted mt-2">
      <SpinnerView small /> Loading listings...
    </div>
  </ModalView>
</template>

<script>
import ModalView from '@/components/common/ModalView.vue';
import RadioCard from '@/components/common/RadioCard.vue';
import SpinnerView from '@/components/common/SpinnerView.vue';
import { fetchApi } from '@/api';
import StatusBadge from "@/components/common/StatusBadge.vue";

export default {
  components: {StatusBadge, ModalView, RadioCard, SpinnerView },
  props: {
    modelValue: { type: Boolean, default: false },
    printOptions: { type: Array, default: () => [] },
    cityId: { type: String, default: null },
    propertyId: { type: String, default: null }
  },
  emits: ['update:modelValue', 'create'],
  data() {
    return {
      selectedTemplateIndex: null,
      selectedListingId: null,
      listings: [],
      loadingListings: false,
      creating: false
    };
  },
  computed: {
    show: {
      get() { return this.modelValue; },
      set(v) { this.$emit('update:modelValue', v); }
    },
    selectedTemplate() {
      return this.printOptions[this.selectedTemplateIndex] || null;
    }
  },
  watch: {
    modelValue(visible) {
      if (visible) {
        this.selectedTemplateIndex = null;
        this.selectedListingId = null;
        this.listings = [];
        this.fetchListings();
      }
    }
  },
  methods: {
    async fetchListings() {
      if (!this.cityId || !this.propertyId) return;
      this.loadingListings = true;
      try {
        const result = await fetchApi(`/v1/cities/${this.cityId}/properties/${this.propertyId}/listings`);
        this.listings = result?.listings || [];
        if (this.listings.length === 1) {
          this.selectedListingId = this.listings[0].id;
        }
      } catch (err) {
        console.error('Failed to fetch listings:', err);
      } finally {
        this.loadingListings = false;
      }
    },
    async onCreate() {
      if (!this.selectedTemplate) return;
      this.creating = true;
      try {
        this.$emit('create', {
          template: this.selectedTemplate.template,
          options: this.selectedTemplate.options,
          listingId: this.selectedListingId
        });
        this.show = false;
      } finally {
        this.creating = false;
      }
    }
  }
};
</script>

<style scoped>
.section-sublabel {
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--bs-secondary-color);
  margin-bottom: 8px;
}
</style>
